/**
 * Every user-facing string for `/stablecoins/invoices/new` (page spec
 * `invoice-create`), in one place.
 *
 * Two rules the copy here keeps, because a create form is where they are
 * easiest to break:
 *
 *  - **No promise the contract cannot keep.** The page never says an invoice
 *    "will be issued" — issuing is a second call the service can refuse
 *    (`INVOICING_NOT_ENABLED`, `KYB_NOT_APPROVED`, `COLLECTION_WALLET_MISSING`,
 *    `DUE_DATE_PAST`, the amount floors). Copy says what the operator asked for
 *    and what came back.
 *  - **Never name a chain, an asset or a wallet in prose.** Assets and
 *    networks come from the settings response
 *    (`settings.collectionWallets`, `settings.networkLimits`); a symbol written
 *    into a sentence here would be a second, unchecked source of truth.
 */

export const COPY = {
  title: "New invoice",
  subtitle: "Compose a stablecoin invoice for an external payer and issue its pay link.",

  /** Section headings. */
  payerHeading: "Payer",
  detailsHeading: "Asset and network",
  linesHeading: "Lines",
  termsHeading: "Terms",
  totalsHeading: "Totals",
  reviewHeading: "Saved draft",

  loading: "Loading invoicing settings and payers…",

  /* ------------------------------------------------------------ refusal copy */

  readDeniedTitle: "Invoicing is not available to this session",
  readDeniedDescription:
    "The gateway refused to read the invoicing settings and payers for this session. Nothing on this page is live: no asset, network or payer is shown, because none was returned.",
  readErrorTitle: "Invoicing settings could not be read",
  readErrorDescription:
    "The invoicing settings and payer list did not answer, so no form is shown. An empty asset list here would be a claim about your organisation that nothing returned.",

  /* ------------------------------------------------------------ payer picker */

  payerLabel: "Payer",
  payerHint: "The payer is the invoice's counterparty record — not an Alloy account.",
  payerPlaceholder: "Select a payer",
  payerNone: "No payer selected",
  payerEmpty: "No payers yet. Add the first payer for this organisation.",
  payerArchivedNote: "Archived payers are not offered on a new invoice.",
  payerRequired: "A payer is required before a draft can be saved.",

  newPayerToggle: "Add a payer",
  newPayerCancel: "Cancel",
  newPayerSubmit: "Add payer",
  newPayerSubmitting: "Adding payer…",
  newPayerHeading: "New payer",
  newPayerName: "Display name",
  newPayerLegalName: "Legal name",
  newPayerEmail: "Billing email",
  newPayerCountry: "Country",
  newPayerTaxId: "Tax identifier",
  newPayerOptional: "Optional",
  newPayerCountryHint: "Two-letter ISO country code, e.g. GB.",
  newPayerNameRequired: "A display name is required.",
  newPayerCountryRequired: "A country is required (two-letter ISO code).",
  newPayerAdded: "Payer added and selected.",
  newPayerCreateDenied: "This session may not add payers.",

  /* -------------------------------------------------------- asset & network */

  assetLabel: "Asset",
  assetHint: "One asset per invoice. The list is every asset your organisation holds a collection wallet for.",
  assetPlaceholder: "Select an asset",
  assetNone: "No asset is configured",
  assetNoneDescription:
    "Your organisation has no collection wallet configured, so there is no asset an invoice could be denominated in. Collection wallets are set up in invoicing settings.",
  assetNoneAction: "Open invoicing settings",

  networkLabel: "Network",
  networkHint:
    "The network the invoice is sized and issued against. Issuing accepts payment on every network your organisation holds a collection wallet for in this asset; launch networks are EVM only.",
  networkPlaceholder: "Select a network",
  /** Terms from the settings response — never a number written here. */
  networkFloorPrefix: "Minimum invoice amount on this network:",
  networkDustPrefix: "Dust threshold on this network:",
  networkAcceptsPrefix: "This invoice will accept payment on:",
  networkAcceptsEmpty: "No network has a collection wallet for this asset yet.",

  /* ------------------------------------------------------------------ lines */

  linesEmptyTitle: "No line items yet",
  linesEmptyDescription:
    "Add at least one line item. A flat tax line and a flat discount line are recorded as their own lines.",
  linesRequired: "Add at least one line item.",
  fixLines: "Fix the highlighted lines before saving.",
  addItem: "Add line item",
  addTax: "Add flat tax",
  addDiscount: "Add flat discount",
  removeLine: "Remove line",

  lineKindItem: "Item",
  lineKindTax: "Tax",
  lineKindDiscount: "Discount",
  lineDescription: "Description",
  lineQuantity: "Quantity",
  lineUnitPrice: "Unit price",
  lineAmount: "Amount",
  lineTotal: "Line total",

  descriptionRequired: "A description is required.",
  quantityRequired: "A quantity is required.",
  unitPriceRequired: "A unit price is required.",
  amountRequired: "An amount is required.",
  /**
   * The contract refuses a line whose quantity × unit price does not land on a
   * whole base unit (`AMOUNT_PRECISION`), so the form reports the same thing
   * rather than rounding — a rounded line here would differ from the ledger.
   */
  linePrecision:
    "Quantity × unit price does not land on a whole base unit for this asset. Adjust the quantity or the unit price; the service refuses a line that has to be rounded.",
  lineZero: "A line amount of zero adds nothing.",
  discountExceedsExtras:
    "The flat discount is larger than the subtotal plus tax. The service refuses a total below zero.",

  /* ------------------------------------------------------------------ terms */

  dueDateLabel: "Due date",
  dueDateHint: "The expiry is the due date plus the organisation's expiry grace.",
  dueDatePast: "This due date is in the past. The service refuses a past due date when the invoice is issued.",
  dueDateClear: "Clear",

  memoLabel: "Memo",
  memoHint: "Shown on the invoice. Never put payer personal data here.",
  memoPlaceholder: "Optional reference for the payer",

  /* ----------------------------------------------------------------- totals */

  subtotal: "Subtotal",
  tax: "Tax",
  discount: "Discount",
  grandTotal: "Grand total",
  totalsInBaseUnits: "Base units",
  totalsBaseUnitHint:
    "Amounts are integer base units on the wire; the token-unit figures above are derived for display only.",

  /* ---------------------------------------------------------------- actions */

  saveDraft: "Save draft",
  saveDraftAgain: "Save draft changes",
  savingDraft: "Saving draft…",
  issue: "Issue invoice",
  issuing: "Issuing…",
  issueRequiresDraft: "Save the draft first: issuing acts on a saved draft and mints its pay link.",
  startOver: "Start over",
  unsavedChanges: "This form has changes the saved draft does not have.",
  /**
   * The contract's draft update carries lines, due date and memo only — a
   * payer or an asset cannot be changed on an existing draft. Saying so is
   * cheaper than a control that silently does nothing.
   */
  lockedNote:
    "The payer and the asset are fixed once the draft exists: the contract's draft update carries lines, due date and memo only. Start over to compose a different invoice.",

  draftSavedTitle: "Draft saved",
  draftSavedDescription:
    "The draft is stored without a number, an address or a pay link. Both appear only when it is issued.",
  draftNumberPending: "Assigned on issue",
  draftIdLabel: "Draft id",

  /** Shown when a control is hidden only because this session's scopes lack it. */
  scopeMissingCreate:
    "This session does not hold the invoice:create scope, so the draft controls are disabled. The gateway decides whether a request is allowed; this is only a courtesy.",
  scopeMissingIssue:
    "This session does not hold the invoice:issue scope, so issuing is disabled. The gateway decides; this is only a courtesy.",
  scopeMissingPayer:
    "This session does not hold the invoice:payer.manage scope, so adding a payer is disabled. The gateway decides; this is only a courtesy.",
  scopeUnknown:
    "This session's effective access has not loaded, so the draft controls stay disabled until it does.",

  /* --------------------------------------------------------- refusal surface */

  refusalHeading: "The request was refused",
  refusalUnknownPrefix: "The service refused this with a code this page does not have copy for:",
  refusalUnknownNote: "Showing it verbatim is honest; inventing an explanation for it would not be.",

  /* ------------------------------------------------------------- pay link */

  issuedHeading: "Invoice issued",
  issuedDescription:
    "The pay link is shown once, here. It is not readable again — copy it now, or rotate it from the invoice if it is lost.",
  payLinkLabel: "Pay link",
  payLinkCopy: "Copy pay link",
  payLinkCopied: "Copied",
  payLinkCopyFailed: "Could not copy — select the link and copy it manually.",
  payLinkNotInUrl:
    "The link is held in memory for this reveal only; it is never logged and never placed in a query string.",
  issuedNumber: "Invoice number",
  issuedAsset: "Asset",
  issuedTotal: "Total",
  issuedDueDate: "Due",
  issuedExpiry: "Expires",
  backToInvoices: "Back to invoices",

  /**
   * The four-eyes boundary, stated on the page itself. This page is a create
   * surface: it never renders an approve or reject control, for a human or for
   * a machine principal.
   */
  fourEyesNote:
    "Approving a resolution or a refund is a separate four-eyes surface, and a machine principal can never approve. No approve control exists on this page.",
} as const;

export type InvoiceCreateCopy = typeof COPY;
